import { mergeClasses, Tooltip } from "@fluentui/react-components";
import {
  Delete20Regular,
  Edit20Regular,
  Eye20Regular,
} from "@fluentui/react-icons";
import { deleteAnnouncement } from "api";
import { ConstantValues } from "common/constants";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { AdminListPage } from "components/adminList/AdminListPage";
import { AdminRowAction } from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { findSidebarItem } from "components/adminSidebar/adminSidebarConfig";
import { useAdminListFilters } from "hooks/useAdminListFilters";
import { useAdminListPagination } from "hooks/useAdminListPagination";
import { useAdminListSearch } from "hooks/useAdminListSearch";
import { useAdminListSort } from "hooks/useAdminListSort";
import { ChevronIcon } from "icons/ChevronIcon";
import { ExportIcon } from "icons/ExportIcon";
import { PlusIcon } from "icons/PlusIcon";
import { SearchIcon } from "icons/SearchIcon";
import {
  ANNOUNCEMENT_TYPE_LABELS,
  ANNOUNCEMENT_TYPES,
  IAnnouncement,
} from "models/IAnnouncement";
import { ITableColumn } from "models/ITableColumn";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import {
  useSharedAdminContentHeaderStyles,
  useSharedButtonStyles,
  useSharedTagStyles,
  useSharedAdminCellStyles,
} from "theme/sharedStyles";
import { downloadCSV, formatDate, generateCSV } from "utils/generalUtils";
import { removeAnnouncement } from "../../redux/announcementsSlice";
import { useManageAnnouncementsStyles } from "./ManageAnnouncements.styles";
import { EditAnnouncement } from "./editDialog/EditAnnouncement";
import { ViewAnnouncement } from "./viewDialog/ViewAnnouncement";

export const ManageAnnouncements = (): JSX.Element => {
  const cellStyles = useManageAnnouncementsStyles();
  const adminCellStyles = useSharedAdminCellStyles();
  const colStyles = useAdminColumnStyles();
  const headerClasses = useSharedAdminContentHeaderStyles();
  const buttonStyles = useSharedButtonStyles();
  const tagStyles = useSharedTagStyles();
  const announcements: IAnnouncement[] = useSelector(
    (state: RootState) => state.announcementsSlice.value,
  );
  const isLoaded: boolean = useSelector(
    (state: RootState) => state.announcementsSlice.isLoaded,
  );
  const dispatch = useDispatch();

  const [editingAnnouncement, setEditingAnnouncement] =
    useState<IAnnouncement | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState<boolean>(false);
  const [viewingAnnouncement, setViewingAnnouncement] =
    useState<IAnnouncement | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [deletingAnnouncement, setDeletingAnnouncement] =
    useState<IAnnouncement | null>(null);
  const { filters, setFilter } = useAdminListFilters({
    cacheKey: "Announcements",
    filterKeys: ["type", "status"] as const,
  });

  const columns: ITableColumn<IAnnouncement>[] = useMemo(
    () => [
      {
        columnId: "title",
        displayValue: "Title",
        className: colStyles.normal,
        renderHeaderCell: () => "Title",
        compare: (a: IAnnouncement, b: IAnnouncement) =>
          a.title.localeCompare(b.title),
        renderCell: (item: IAnnouncement) => (
          <Tooltip content={item.title} relationship="label">
            <span className={adminCellStyles.nameText}>{item.title}</span>
          </Tooltip>
        ),
      },
      {
        columnId: "content",
        displayValue: "Content",
        className: colStyles.wide,
        renderHeaderCell: () => "Content",
        compare: (a: IAnnouncement, b: IAnnouncement) =>
          a.content.localeCompare(b.content),
        renderCell: (item: IAnnouncement) => (
          <Tooltip
            content={
              new DOMParser().parseFromString(item.content, "text/html").body
                .textContent ?? ConstantValues.EMPTY_STRING
            }
            relationship="label"
          >
            <div
              className={cellStyles.htmlContent}
              dangerouslySetInnerHTML={{ __html: item.content || "" }}
            />
          </Tooltip>
        ),
      },
      {
        columnId: "type",
        displayValue: "Type",
        className: colStyles.normal,
        renderHeaderCell: () => "Type",
        compare: (a: IAnnouncement, b: IAnnouncement) =>
          (a.type || ANNOUNCEMENT_TYPES.INFORMATIONAL).localeCompare(
            b.type || ANNOUNCEMENT_TYPES.INFORMATIONAL,
          ),
        renderCell: (item: IAnnouncement) => (
          <span
            className={mergeClasses(
              tagStyles.tag,
              item.type === ANNOUNCEMENT_TYPES.WARNING
                ? tagStyles.tagWarning
                : tagStyles.tagSuccess,
            )}
          >
            {item.type === ANNOUNCEMENT_TYPES.WARNING
              ? ANNOUNCEMENT_TYPE_LABELS[ANNOUNCEMENT_TYPES.WARNING]
              : ANNOUNCEMENT_TYPE_LABELS[ANNOUNCEMENT_TYPES.INFORMATIONAL]}
          </span>
        ),
      },
      {
        columnId: "updatedAt",
        displayValue: "Last Updated",
        className: colStyles.normal,
        renderHeaderCell: () => "Last Updated",
        compare: (a: IAnnouncement, b: IAnnouncement) =>
          a.updatedAt - b.updatedAt,
        renderCell: (item: IAnnouncement) => (
          <span>{formatDate(item.updatedAt)}</span>
        ),
      },
      {
        columnId: "displayName",
        displayValue: "Author",
        className: colStyles.medium,
        renderHeaderCell: () => "Author",
        compare: (a: IAnnouncement, b: IAnnouncement) =>
          (a.displayName ?? ConstantValues.EMPTY_STRING).localeCompare(
            b.displayName ?? ConstantValues.EMPTY_STRING,
          ),
        renderCell: (item: IAnnouncement) => (
          <Tooltip
            content={item.displayName || ConstantValues.EMPTY_STRING}
            relationship="label"
          >
            <span>{item.displayName || null}</span>
          </Tooltip>
        ),
      },
      {
        columnId: "enabled",
        displayValue: "Enabled",
        className: colStyles.medium,
        renderHeaderCell: () => "Enabled",
        compare: (a: IAnnouncement, b: IAnnouncement) =>
          Number(a.enabled) - Number(b.enabled),
        renderCell: (item: IAnnouncement) => <>{item.enabled ? "Yes" : "No"}</>,
      },
    ],
    [
      adminCellStyles.nameText,
      cellStyles.htmlContent,
      colStyles.normal,
      colStyles.wide,
      colStyles.medium,
      tagStyles.tag,
      tagStyles.tagWarning,
      tagStyles.tagSuccess,
    ],
  );

  const {
    sortColumn,
    isSortDescending,
    sortItems,
    onSortColumnChanged,
    onClearColumnChanged,
  } = useAdminListSort<IAnnouncement>({
    cacheKey: "Announcements",
    defaultSortColumn: "updatedAt",
    defaultDescending: true,
  });

  const searchFields = useMemo(
    () => ["title", "content", "displayName"] as (keyof IAnnouncement)[],
    [],
  );
  const {
    searchQuery: internalSearchQuery,
    handleSearchChange,
    handleSearchValueChange,
    filteredItems,
  } = useAdminListSearch<IAnnouncement>({
    items: announcements,
    searchFields,
  });

  const typeOptions = useMemo(
    () => [
      { key: ANNOUNCEMENT_TYPES.INFORMATIONAL, text: "Informational" },
      { key: ANNOUNCEMENT_TYPES.WARNING, text: "Warning" },
    ],
    [],
  );

  const statusOptions = useMemo(
    () => [
      { key: "enabled", text: "Enabled" },
      { key: "disabled", text: "Disabled" },
    ],
    [],
  );

  const fullyFilteredItems = useMemo(() => {
    let result = filteredItems;
    if (filters.type)
      result = result.filter((ann) => ann.type === filters.type);
    if (filters.status)
      result = result.filter((ann) =>
        filters.status === "enabled"
          ? ann.enabled === true
          : ann.enabled !== true,
      );
    return result;
  }, [filteredItems, filters.type, filters.status]);

  const sortedItems = useMemo(
    () => sortItems(fullyFilteredItems, columns),
    [fullyFilteredItems, sortItems, columns],
  );

  const emptyStateMessage = useMemo(() => {
    const hasActiveFilters =
      internalSearchQuery || filters.type || filters.status;
    return hasActiveFilters
      ? "No announcements match the current filters."
      : 'No announcements found. Use "Add New" to create one.';
  }, [internalSearchQuery, filters.type, filters.status]);

  const handleExport = useCallback((): void => {
    const csv = generateCSV(sortedItems, [
      { header: "Title", accessor: (a) => a.title },
      {
        header: "Content",
        accessor: (a) =>
          new DOMParser().parseFromString(a.content || "", "text/html").body
            .textContent ?? "",
      },
      {
        header: "Type",
        accessor: (a) =>
          a.type === ANNOUNCEMENT_TYPES.WARNING
            ? ANNOUNCEMENT_TYPE_LABELS[ANNOUNCEMENT_TYPES.WARNING]
            : ANNOUNCEMENT_TYPE_LABELS[ANNOUNCEMENT_TYPES.INFORMATIONAL],
      },
      { header: "Last Updated", accessor: (a) => formatDate(a.updatedAt) },
      { header: "Author", accessor: (a) => a.displayName || "" },
      { header: "Enabled", accessor: (a) => (a.enabled ? "Yes" : "No") },
    ]);
    downloadCSV(csv, "announcements.csv");
  }, [sortedItems]);

  const {
    paginatedItems,
    selectedPageIndex,
    pageCount,
    handlePageChange,
    totalItemsCount,
    itemsPerPage,
  } = useAdminListPagination<IAnnouncement>({
    items: sortedItems,
    itemsPerPage: 10,
  });

  const handleViewAnnouncement = (announcement: IAnnouncement) => {
    setViewingAnnouncement(announcement);
    setIsViewDialogOpen(true);
  };

  const handleViewDismiss = () => {
    setIsViewDialogOpen(false);
    setViewingAnnouncement(null);
  };

  const handleEditAnnouncement = (announcement: IAnnouncement) => {
    setEditingAnnouncement(announcement);
    setIsEditDialogOpen(true);
  };

  const handleAddAnnouncement = () => {
    setEditingAnnouncement(null);
    setIsEditDialogOpen(true);
  };

  const handleDeleteAnnouncement = (announcement: IAnnouncement) => {
    setDeletingAnnouncement(announcement);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingAnnouncement) return;
    try {
      await deleteAnnouncement({ id: deletingAnnouncement.id });
      dispatch(
        removeAnnouncement(
          deletingAnnouncement.id ?? ConstantValues.EMPTY_STRING,
        ),
      );
    } catch (error) {
      console.error("Failed to delete announcement:", error);
    } finally {
      setIsDeleteDialogOpen(false);
      setDeletingAnnouncement(null);
    }
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingAnnouncement(null);
  };

  const handleEditSuccess = () => {
    handleCloseEditDialog();
  };

  const rowActions: AdminRowAction<IAnnouncement>[] = useMemo(
    () => [
      {
        icon: <Eye20Regular />,
        tooltip: "View",
        onClick: handleViewAnnouncement,
      },
      {
        icon: <Edit20Regular />,
        tooltip: "Edit",
        onClick: handleEditAnnouncement,
      },
      {
        icon: <Delete20Regular />,
        tooltip: "Delete",
        onClick: handleDeleteAnnouncement,
      },
    ],
    [],
  );

  const sidebarItem = useMemo(() => findSidebarItem("announcements"), []);

  return (
    <>
      <div className={headerClasses.root}>
        <div
          className={mergeClasses(
            headerClasses.titleRow,
            headerClasses.titleRowWithFilters,
          )}
        >
          <h1 className={headerClasses.title}>
            {sidebarItem?.label || "Announcements"}
          </h1>
          <div className={headerClasses.actions}>
            <button
              className={mergeClasses(
                headerClasses.actionButton,
                buttonStyles.secondaryButton,
              )}
              onClick={handleExport}
              type="button"
            >
              <ExportIcon /> Export
            </button>
            <button
              className={mergeClasses(
                headerClasses.actionButton,
                buttonStyles.primaryButton,
              )}
              onClick={handleAddAnnouncement}
              type="button"
            >
              <PlusIcon /> Add New
            </button>
          </div>
        </div>
        <div className={headerClasses.filterRow}>
          <div className={headerClasses.searchInputWrapper}>
            <SearchIcon className={headerClasses.searchIcon} />
            <input
              type="text"
              className={headerClasses.searchInput}
              placeholder="Search announcements..."
              value={internalSearchQuery}
              onChange={(e) => handleSearchValueChange(e.target.value)}
              aria-label="Search announcements..."
            />
          </div>
          <div className={headerClasses.filterSelectWrapper}>
            <select
              className={headerClasses.filterSelect}
              value={filters.type}
              onChange={(e) => setFilter("type", e.target.value)}
              aria-label="All Types"
            >
              <option value="">All Types</option>
              {typeOptions.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.text}
                </option>
              ))}
            </select>
            <ChevronIcon className={headerClasses.filterChevron} />
          </div>
          <div className={headerClasses.filterSelectWrapper}>
            <select
              className={headerClasses.filterSelect}
              value={filters.status}
              onChange={(e) => setFilter("status", e.target.value)}
              aria-label="All Status"
            >
              <option value="">All Status</option>
              {statusOptions.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.text}
                </option>
              ))}
            </select>
            <ChevronIcon className={headerClasses.filterChevron} />
          </div>
        </div>
      </div>
      <AdminListPage<IAnnouncement>
        title="Announcements"
        items={announcements}
        columns={columns}
        isLoading={!isLoaded}
        headerActions={[]}
        searchPlaceholder="Search announcements..."
        searchQuery={internalSearchQuery}
        onSearchChange={handleSearchChange}
        hideHeader={true}
        rowActions={rowActions}
        getRowId={(announcement) =>
          announcement.id || ConstantValues.EMPTY_STRING
        }
        sortColumn={sortColumn}
        isSortDescending={isSortDescending}
        onSortChange={onSortColumnChanged}
        onSortClear={onClearColumnChanged}
        paginatedItems={paginatedItems}
        selectedPageIndex={selectedPageIndex}
        pageCount={pageCount}
        totalItemsCount={totalItemsCount}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        emptyStateMessage={emptyStateMessage}
      />

      {isViewDialogOpen && viewingAnnouncement && (
        <ViewAnnouncement
          isOpen={isViewDialogOpen}
          onDismiss={handleViewDismiss}
          announcement={viewingAnnouncement}
        />
      )}

      {isEditDialogOpen && (
        <EditAnnouncement
          announcement={editingAnnouncement}
          isOpen={isEditDialogOpen}
          onDismiss={(isSaved): void => {
            if (isSaved) {
              handleEditSuccess();
            } else {
              handleCloseEditDialog();
            }
          }}
        />
      )}

      <ActionDialog
        isVisible={isDeleteDialogOpen}
        title="Delete Announcement"
        message={`Are you sure you want to permanently delete "${deletingAnnouncement?.title}"? This action cannot be undone.`}
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={handleConfirmDelete}
        onDismiss={() => {
          setIsDeleteDialogOpen(false);
          setDeletingAnnouncement(null);
        }}
      />
    </>
  );
};
